import dayjs, { Dayjs, ManipulateType } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Timestamp } from 'firebase/firestore';
import { Application, Referrer } from 'types';
import { WeeklySchedule, TimeSlot } from 'types';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

export const defaultWeeklySchedule: WeeklySchedule = {
  monday: { enabled: false, timeSlots: [] },
  tuesday: { enabled: false, timeSlots: [] },
  wednesday: { enabled: false, timeSlots: [] },
  thursday: { enabled: false, timeSlots: [] },
  friday: { enabled: false, timeSlots: [] },
  saturday: { enabled: false, timeSlots: [] },
  sunday: { enabled: false, timeSlots: [] },
};

export type AxisData = {
    x: string[];
    y: number[];
};
export type Data = Application | Referrer;
export const getNumByScale = (data: Timestamp[], startTime: Dayjs, endTime: Dayjs, scale: ManipulateType): AxisData => {
    const timeMap:{ [key: string]: number }  = {};
    const diff = endTime.startOf(scale).diff(startTime.startOf(scale), scale) + 1;
    for (let i = 0; i < diff; i++) {
        timeMap[startTime.startOf(scale).add(i, scale).format('YYYY-MM-DD')] =  0;
    }
    data.forEach((item) => {
        const time = dayjs(item.toDate()).startOf(scale).format('YYYY-MM-DD');
        timeMap[time] =  (timeMap[time] || 0) + 1;
    })
    return {
        x: Object.keys(timeMap),
        y: Object.values(timeMap),
    } as AxisData;
};

export const validScale = (startTime: Dayjs, endTime: Dayjs, scale: ManipulateType): boolean => {
    return endTime.startOf(scale).diff(startTime.startOf(scale), scale) > 0;
}

/**
 * Formats a time slot to a readable string
 */
export const formatTimeSlot = (timeSlot: TimeSlot): string => {
    return `${timeSlot.startTime}-${timeSlot.endTime}`;
};

/**
 * Formats a weekly schedule to a readable string
 */
export const formatWeeklySchedule = (schedule: WeeklySchedule): string => {
    const dayNames = {
        monday: 'Monday',
        tuesday: 'Tuesday', 
        wednesday: 'Wednesday',
        thursday: 'Thursday',
        friday: 'Friday',
        saturday: 'Saturday',
        sunday: 'Sunday'
    };

    const enabledDays = Object.entries(schedule)
        .filter(([_, daySchedule]) => daySchedule.enabled && daySchedule.timeSlots.length > 0)
        .map(([dayKey, daySchedule]) => {
            const dayName = dayNames[dayKey as keyof typeof dayNames];
            const timeSlots = daySchedule.timeSlots.map(formatTimeSlot).join(', ');
            return `${dayName} ${timeSlots}`;
        });

    return enabledDays.length > 0 ? enabledDays.join('; ') : 'No availability set';
};

/**
 * Checks if a weekly schedule object is valid (has at least one enabled day with time slots)
 */
export const isValidWeeklySchedule = (schedule: WeeklySchedule): boolean => {
    return Object.values(schedule).some(daySchedule => 
        daySchedule.enabled && daySchedule.timeSlots.length > 0
    );
};

export interface AvailableTimeSlot {
    date: string; // YYYY-MM-DD format
    dayName: string; // Monday, Tuesday, etc.
    startTime: string; // HH:mm format
    endTime: string; // HH:mm format
    displayDate: string; // "Mon, Sep 25"
}

export interface AppointmentSlot {
    date: string; // YYYY-MM-DD format
    dayName: string; // Monday, Tuesday, etc.
    startTime: string; // HH:mm format in mentor's timezone
    endTime: string; // HH:mm format in mentor's timezone
    displayDate: string; // "Mon, Sep 25"
    displayTime: string; // "15:00 - 15:30"
    mentorTimezone: string; // e.g., "Asia/Taipei"
    studentTimezone?: string; // e.g., "America/New_York"
    mentorDisplayTime: string; // "15:00 - 15:30 (UTC+8)"
    studentDisplayTime?: string; // "23:00 - 23:30 (UTC-5)" - converted time
}

/**
 * Generates 30-minute appointment slots from a time range with timezone support
 */
export const generate30MinuteSlots = (
    startTime: string, 
    endTime: string, 
    date: string, 
    dayName: string, 
    displayDate: string,
    mentorTimezone: string = dayjs.tz.guess(),
    studentTimezone?: string
): AppointmentSlot[] => {
    const slots: AppointmentSlot[] = [];
    
    // Parse start and end times
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    
    // Convert to minutes for easier calculation
    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;
    
    // Generate 30-minute slots
    for (let currentMinutes = startTotalMinutes; currentMinutes + 30 <= endTotalMinutes; currentMinutes += 30) {
        const slotStartHour = Math.floor(currentMinutes / 60);
        const slotStartMinute = currentMinutes % 60;
        const slotEndHour = Math.floor((currentMinutes + 30) / 60);
        const slotEndMinute = (currentMinutes + 30) % 60;
        
        const slotStartTime = `${slotStartHour.toString().padStart(2, '0')}:${slotStartMinute.toString().padStart(2, '0')}`;
        const slotEndTime = `${slotEndHour.toString().padStart(2, '0')}:${slotEndMinute.toString().padStart(2, '0')}`;
        
        // Create datetime objects for timezone conversion
        const mentorStartDateTime = dayjs.tz(`${date} ${slotStartTime}`, mentorTimezone);
        const mentorEndDateTime = dayjs.tz(`${date} ${slotEndTime}`, mentorTimezone);
        
        // Get timezone offset display for mentor
        const mentorOffset = mentorStartDateTime.format('Z');
        const mentorDisplayTime = `${slotStartTime} - ${slotEndTime} (UTC${mentorOffset})`;
        
        // Convert to student timezone if provided
        let studentDisplayTime: string | undefined;
        if (studentTimezone) {
            const studentStartDateTime = mentorStartDateTime.tz(studentTimezone);
            const studentEndDateTime = mentorEndDateTime.tz(studentTimezone);
            const studentOffset = studentStartDateTime.format('Z');
            studentDisplayTime = `${studentStartDateTime.format('HH:mm')} - ${studentEndDateTime.format('HH:mm')} (UTC${studentOffset})`;
        }
        
        slots.push({
            date,
            dayName,
            startTime: slotStartTime,
            endTime: slotEndTime,
            displayDate,
            displayTime: `${slotStartTime} - ${slotEndTime}`,
            mentorTimezone,
            studentTimezone,
            mentorDisplayTime,
            studentDisplayTime
        });
    }
    
    return slots;
};

/**
 * Generates 30-minute appointment slots for the next two weeks based on mentor's weekly schedule
 */
export const generateAppointmentSlots = (schedule: WeeklySchedule, mentorTimezone: string = dayjs.tz.guess(), studentTimezone?: string): AppointmentSlot[] => {
    const appointmentSlots: AppointmentSlot[] = [];
    const today = dayjs().tz(mentorTimezone);
    
    // Map day names to dayjs day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayMapping = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
    };

    // Generate slots for the next 14 days
    for (let i = 0; i < 14; i++) {
        const currentDate = today.add(i, 'day');
        const dayOfWeek = currentDate.day();
        
        // Find matching day in schedule
        const scheduleDay = Object.entries(dayMapping).find(([_, value]) => value === dayOfWeek)?.[0];
        
        if (scheduleDay && schedule[scheduleDay as keyof WeeklySchedule]?.enabled) {
            const daySchedule = schedule[scheduleDay as keyof WeeklySchedule];
            
            daySchedule.timeSlots.forEach(timeSlot => {
                // Generate 30-minute slots for this time range
                const thirtyMinSlots = generate30MinuteSlots(
                    timeSlot.startTime,
                    timeSlot.endTime,
                    currentDate.format('YYYY-MM-DD'),
                    currentDate.format('dddd'),
                    currentDate.format('ddd, MMM D'),
                    mentorTimezone,
                    studentTimezone
                );
                
                appointmentSlots.push(...thirtyMinSlots);
            });
        }
    }
    
    return appointmentSlots;
};

/**
 * Generates time options in 30-minute increments from 00:00 to 23:30
 * Returns array of time strings: ['00:00', '00:30', '01:00', '01:30', ...]
 */
export const generateTimeOptions = (): string[] => {
    const times: string[] = [];
    
    for (let hour = 0; hour < 24; hour++) {
        // Add the hour (e.g., 12:00)
        const hourString = hour.toString().padStart(2, '0');
        times.push(`${hourString}:00`);
        
        // Add the half-hour (e.g., 12:30)
        times.push(`${hourString}:30`);
    }
    
    return times;
};

/**
 * Formats a time string for display (e.g., "09:00" -> "9:00 AM", "15:30" -> "3:30 PM")
 */
export const formatTimeDisplay = (time: string): string => {
    const [hour, minute] = time.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

// Comprehensive timezone list organized by regions
export const TIMEZONE_OPTIONS = [
    // Asia Pacific
    { value: 'Asia/Tokyo', label: '(UTC+09:00) Tokyo, Japan', region: 'Asia Pacific' },
    { value: 'Asia/Seoul', label: '(UTC+09:00) Seoul, South Korea', region: 'Asia Pacific' },
    { value: 'Asia/Shanghai', label: '(UTC+08:00) Beijing, Shanghai, China', region: 'Asia Pacific' },
    { value: 'Asia/Taipei', label: '(UTC+08:00) Taipei, Taiwan', region: 'Asia Pacific' },
    { value: 'Asia/Hong_Kong', label: '(UTC+08:00) Hong Kong', region: 'Asia Pacific' },
    { value: 'Asia/Singapore', label: '(UTC+08:00) Singapore', region: 'Asia Pacific' },
    { value: 'Asia/Bangkok', label: '(UTC+07:00) Bangkok, Thailand', region: 'Asia Pacific' },
    { value: 'Asia/Jakarta', label: '(UTC+07:00) Jakarta, Indonesia', region: 'Asia Pacific' },
    { value: 'Asia/Manila', label: '(UTC+08:00) Manila, Philippines', region: 'Asia Pacific' },
    { value: 'Asia/Kuala_Lumpur', label: '(UTC+08:00) Kuala Lumpur, Malaysia', region: 'Asia Pacific' },
    { value: 'Asia/Kolkata', label: '(UTC+05:30) Mumbai, Delhi, India', region: 'Asia Pacific' },
    { value: 'Asia/Dubai', label: '(UTC+04:00) Dubai, UAE', region: 'Asia Pacific' },
    
    // North America
    { value: 'America/New_York', label: '(UTC-05:00) Eastern Time (New York)', region: 'North America' },
    { value: 'America/Chicago', label: '(UTC-06:00) Central Time (Chicago)', region: 'North America' },
    { value: 'America/Denver', label: '(UTC-07:00) Mountain Time (Denver)', region: 'North America' },
    { value: 'America/Los_Angeles', label: '(UTC-08:00) Pacific Time (Los Angeles)', region: 'North America' },
    { value: 'America/Toronto', label: '(UTC-05:00) Toronto, Canada', region: 'North America' },
    { value: 'America/Vancouver', label: '(UTC-08:00) Vancouver, Canada', region: 'North America' },
    
    // Europe
    { value: 'Europe/London', label: '(UTC+00:00) London, UK', region: 'Europe' },
    { value: 'Europe/Paris', label: '(UTC+01:00) Paris, France', region: 'Europe' },
    { value: 'Europe/Berlin', label: '(UTC+01:00) Berlin, Germany', region: 'Europe' },
    { value: 'Europe/Rome', label: '(UTC+01:00) Rome, Italy', region: 'Europe' },
    { value: 'Europe/Madrid', label: '(UTC+01:00) Madrid, Spain', region: 'Europe' },
    { value: 'Europe/Amsterdam', label: '(UTC+01:00) Amsterdam, Netherlands', region: 'Europe' },
    { value: 'Europe/Moscow', label: '(UTC+03:00) Moscow, Russia', region: 'Europe' },
    
    // Oceania
    { value: 'Australia/Sydney', label: '(UTC+10:00) Sydney, Australia', region: 'Oceania' },
    { value: 'Australia/Melbourne', label: '(UTC+10:00) Melbourne, Australia', region: 'Oceania' },
    { value: 'Australia/Perth', label: '(UTC+08:00) Perth, Australia', region: 'Oceania' },
    { value: 'Pacific/Auckland', label: '(UTC+12:00) Auckland, New Zealand', region: 'Oceania' },
    
    // South America
    { value: 'America/Sao_Paulo', label: '(UTC-03:00) São Paulo, Brazil', region: 'South America' },
    { value: 'America/Mexico_City', label: '(UTC-06:00) Mexico City, Mexico', region: 'North America' },
    { value: 'America/Argentina/Buenos_Aires', label: '(UTC-03:00) Buenos Aires, Argentina', region: 'South America' },
    
    // Africa
    { value: 'Africa/Cairo', label: '(UTC+02:00) Cairo, Egypt', region: 'Africa' },
    { value: 'Africa/Johannesburg', label: '(UTC+02:00) Johannesburg, South Africa', region: 'Africa' },
    { value: 'Africa/Lagos', label: '(UTC+01:00) Lagos, Nigeria', region: 'Africa' },
];

/**
 * Get timezone options grouped by region for better UX
 */
export const getTimezoneOptionsByRegion = () => {
    const grouped = TIMEZONE_OPTIONS.reduce((acc, tz) => {
        if (!acc[tz.region]) {
            acc[tz.region] = [];
        }
        acc[tz.region].push(tz);
        return acc;
    }, {} as Record<string, typeof TIMEZONE_OPTIONS>);
    
    return grouped;
};

/**
 * Generates available time slots for the next two weeks based on mentor's weekly schedule
 */
export const generateAvailableTimeSlots = (schedule: WeeklySchedule): AvailableTimeSlot[] => {
    const slots: AvailableTimeSlot[] = [];
    const today = dayjs();
    
    // Map day names to dayjs day numbers (0 = Sunday, 1 = Monday, etc.)
    const dayMapping = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
    };

    // Generate slots for the next 14 days
    for (let i = 0; i < 14; i++) {
        const currentDate = today.add(i, 'day');
        const dayOfWeek = currentDate.day();
        
        // Find matching day in schedule
        const scheduleDay = Object.entries(dayMapping).find(([_, value]) => value === dayOfWeek)?.[0];
        
        if (scheduleDay && schedule[scheduleDay as keyof WeeklySchedule]?.enabled) {
            const daySchedule = schedule[scheduleDay as keyof WeeklySchedule];
            
            daySchedule.timeSlots.forEach(timeSlot => {
                slots.push({
                    date: currentDate.format('YYYY-MM-DD'),
                    dayName: currentDate.format('dddd'),
                    startTime: timeSlot.startTime,
                    endTime: timeSlot.endTime,
                    displayDate: currentDate.format('ddd, MMM D')
                });
            });
        }
    }
    
    return slots;
};